import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, PERMISSIONS } from '@/contexts/AuthContext'
import { AccountProvider } from '@/contexts/AccountContext'
import { AuthGuard } from '@/components/AuthGuard'
import { PermissionGuard } from '@/components/PermissionGuard'
import { AdminLayout } from '@/layouts/AdminLayout'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ExecutionListPage } from '@/features/execution-monitoring/pages/ExecutionListPage'
import { ExecutionDetailsPage } from '@/features/execution-monitoring/pages/ExecutionDetailsPage'
import { FinanceDashboard } from '@/pages/FinanceDashboard'
import { AccountUsagePage } from '@/pages/AccountUsagePage'
import { DeveloperConsolePage } from '@/features/developer-console/pages/DeveloperConsolePage'
import { MongoConsolePage } from '@/features/mongo-console/pages/MongoConsolePage'
import { RedisConsolePage } from '@/features/redis-console/pages/RedisConsolePage'
import { DataIngestionPage } from '@/features/data-ingestion/pages/DataIngestionPage'
import { ApiKeyManagementPage } from '@/features/api-keys/pages/ApiKeyManagementPage'
import { BatchJobDashboardPage } from '@/features/batch-job-monitoring/pages/BatchJobDashboardPage'
import { BatchJobHistoryPage } from '@/features/batch-job-monitoring/pages/BatchJobHistoryPage'
import { BatchJobDetailPage } from '@/features/batch-job-monitoring/pages/BatchJobDetailPage'
import { AccountManagementPage } from '@/features/accounts/pages/AccountManagementPage'
import { SyncValidationPage } from '@/features/sync-validation/pages/SyncValidationPage'
import { ParityMonitorPage } from '@/features/parity-monitor/pages/ParityMonitorPage'
import { PipelineMonitoringPage } from '@/features/pipeline-monitoring/pages/PipelineMonitoringPage'
import { PipelineTopologyPage } from '@/features/pipeline-monitoring/pages/PipelineTopologyPage'
import { TopicMessagesPage } from '@/features/pipeline-monitoring/pages/TopicMessagesPage'
import { MessageTracePage } from '@/features/pipeline-monitoring/pages/MessageTracePage'
import { EventTracePage } from '@/features/event-trace/pages/EventTracePage'
import NonUnifiableMonitoringPage from '@/features/non-unifiable-monitoring/pages/NonUnifiableMonitoringPage'
import { DerivationConfigPage } from '@/features/derivation-config/pages/DerivationConfigPage'
import { ProductDataPage } from '@/features/product-data/pages/ProductDataPage'
import { ConsumablesReportPage } from '@/features/finance/pages/ConsumablesReportPage'
import { AttributionReportPage } from '@/features/execution-monitoring/pages/AttributionReportPage'
import { UsageAdjustmentsPage } from '@/features/usage-adjustments/pages/UsageAdjustmentsPage'
import { ConsumableBalancesPage } from '@/features/consumable-balances/pages/ConsumableBalancesPage'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AccountProvider>
          <BrowserRouter>
            <Routes>
              {/* Public route - Login */}
              <Route path="/login" element={<LoginPage />} />

            {/* Protected routes - require authentication */}
            <Route element={<AuthGuard />}>
              <Route element={<AdminLayout />}>
                {/* Dashboard - visible to users with any dashboard permission */}
                <Route path="/" element={
                  <PermissionGuard permissions={[PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD, PERMISSIONS.FINANCE_MONITORING_DASHBOARD]}>
                    <DashboardPage />
                  </PermissionGuard>
                } />

                {/* Attribution Reports - requires CAMPAIGN_MONITORING_DASHBOARD */}
                <Route path="/attribution" element={
                  <PermissionGuard permissions={[PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD]}>
                    <AttributionReportPage />
                  </PermissionGuard>
                } />

                {/* Campaign Execution Monitoring - requires CAMPAIGN_MONITORING_DASHBOARD */}
                <Route path="/executions" element={
                  <PermissionGuard permissions={[PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD]}>
                    <ExecutionListPage />
                  </PermissionGuard>
                } />
                <Route path="/executions/:executionId" element={
                  <PermissionGuard permissions={[PERMISSIONS.CAMPAIGN_MONITORING_DASHBOARD]}>
                    <ExecutionDetailsPage />
                  </PermissionGuard>
                } />

                {/* Finance Dashboard - requires FINANCE_MONITORING_DASHBOARD */}
                <Route path="/finance" element={
                  <PermissionGuard permissions={[PERMISSIONS.FINANCE_MONITORING_DASHBOARD]}>
                    <FinanceDashboard />
                  </PermissionGuard>
                } />
                <Route path="/finance/account/:accountId" element={
                  <PermissionGuard permissions={[PERMISSIONS.FINANCE_MONITORING_DASHBOARD]}>
                    <AccountUsagePage />
                  </PermissionGuard>
                } />
                <Route path="/finance/consumables" element={
                  <PermissionGuard permissions={[PERMISSIONS.FINANCE_MONITORING_DASHBOARD]}>
                    <ConsumablesReportPage />
                  </PermissionGuard>
                } />
                <Route path="/finance/adjustments" element={
                  <PermissionGuard permissions={[PERMISSIONS.FINANCE_MONITORING_DASHBOARD]}>
                    <UsageAdjustmentsPage />
                  </PermissionGuard>
                } />
                <Route path="/finance/balances" element={
                  <PermissionGuard permissions={[PERMISSIONS.FINANCE_MONITORING_DASHBOARD]}>
                    <ConsumableBalancesPage />
                  </PermissionGuard>
                } />

                {/* Developer Console - requires DEVELOPER_CONSOLE */}
                <Route path="/developer-console" element={
                  <PermissionGuard permissions={[PERMISSIONS.DEVELOPER_CONSOLE]}>
                    <DeveloperConsolePage />
                  </PermissionGuard>
                } />

                {/* Derivation Config - requires DERIVATION_CONFIG */}
                <Route path="/derivation-config" element={
                  <PermissionGuard permissions={[PERMISSIONS.DERIVATION_CONFIG]}>
                    <DerivationConfigPage />
                  </PermissionGuard>
                } />

                {/* Data Ingestion - requires DATA_INGESTION */}
                <Route path="/data-ingestion" element={
                  <PermissionGuard permissions={[PERMISSIONS.DATA_INGESTION]}>
                    <DataIngestionPage />
                  </PermissionGuard>
                } />

                {/* Placeholder routes for future features */}
                <Route path="/mongodb" element={
                  <PermissionGuard permissions={[PERMISSIONS.MONGO_CONSOLE_VIEW]}>
                    <MongoConsolePage />
                  </PermissionGuard>
                } />
                <Route path="/redis-console" element={
                  <PermissionGuard permissions={[PERMISSIONS.REDIS_CONSOLE]}>
                    <RedisConsolePage />
                  </PermissionGuard>
                } />
                <Route path="/api-keys" element={
                  <PermissionGuard permissions={[PERMISSIONS.API_KEY_MANAGEMENT]}>
                    <ApiKeyManagementPage />
                  </PermissionGuard>
                } />
                <Route path="/batch-jobs" element={
                  <PermissionGuard permissions={[PERMISSIONS.BATCH_JOB_MONITORING]}>
                    <BatchJobDashboardPage />
                  </PermissionGuard>
                } />
                <Route path="/batch-jobs/history" element={
                  <PermissionGuard permissions={[PERMISSIONS.BATCH_JOB_MONITORING]}>
                    <BatchJobHistoryPage />
                  </PermissionGuard>
                } />
                <Route path="/batch-jobs/:executionId" element={
                  <PermissionGuard permissions={[PERMISSIONS.BATCH_JOB_MONITORING]}>
                    <BatchJobDetailPage />
                  </PermissionGuard>
                } />
                <Route path="/accounts" element={
                  <PermissionGuard permissions={[PERMISSIONS.ACCOUNT_MANAGEMENT]}>
                    <AccountManagementPage />
                  </PermissionGuard>
                } />
                <Route path="/sync-validation" element={
                  <PermissionGuard permissions={[PERMISSIONS.SYNC_MONITORING]}>
                    <SyncValidationPage />
                  </PermissionGuard>
                } />
                <Route path="/parity-monitor" element={
                  <PermissionGuard permissions={[PERMISSIONS.SYNC_MONITORING]}>
                    <ParityMonitorPage />
                  </PermissionGuard>
                } />
                <Route path="/pipeline-monitoring" element={
                  <PermissionGuard permissions={[PERMISSIONS.PIPELINE_MONITORING]}>
                    <PipelineMonitoringPage />
                  </PermissionGuard>
                } />
                <Route path="/pipeline-topology" element={
                  <PermissionGuard permissions={[PERMISSIONS.PIPELINE_MONITORING]}>
                    <PipelineTopologyPage />
                  </PermissionGuard>
                } />
                <Route path="/pipeline-topology/topic/:topicName" element={
                  <PermissionGuard permissions={[PERMISSIONS.PIPELINE_MONITORING]}>
                    <TopicMessagesPage />
                  </PermissionGuard>
                } />
                <Route path="/message-trace" element={
                  <PermissionGuard permissions={[PERMISSIONS.PIPELINE_MONITORING]}>
                    <MessageTracePage />
                  </PermissionGuard>
                } />
                <Route path="/event-trace" element={
                  <PermissionGuard permissions={[PERMISSIONS.PIPELINE_MONITORING]}>
                    <EventTracePage />
                  </PermissionGuard>
                } />
                <Route path="/non-unifiable-monitoring" element={
                  <PermissionGuard permissions={[PERMISSIONS.NON_UNIFIABLE_MONITORING]}>
                    <NonUnifiableMonitoringPage />
                  </PermissionGuard>
                } />
                <Route path="/product-data" element={
                  <PermissionGuard permissions={[PERMISSIONS.PRODUCT_DATA]}>
                    <ProductDataPage />
                  </PermissionGuard>
                } />
                <Route path="/users" element={
                  <PermissionGuard permissions={[PERMISSIONS.USER_MANAGEMENT]}>
                    <ComingSoonPage title="User Management" />
                  </PermissionGuard>
                } />
                <Route path="/settings" element={<ComingSoonPage title="Settings" />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </AccountProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Generic coming soon page
function ComingSoonPage({ title }: { title: string }) {
  return (
    <div className="p-6">
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">🚧</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-500 mt-2">This feature is coming soon!</p>
      </div>
    </div>
  )
}

export default App
